import React from 'react';
import { ResourceField, ResourceFormPage } from '../../components/resource/ResourceFormPage';
import { TratamentoMadeira, ResultadoTratamento } from '../../types/aquisicao';
import {
  lotesMadeiraService,
  tiposTratamentoService,
  tratamentosMadeiraService,
} from '../../services';
import { makeOptionLoader } from '../../utils/optionLoaders';

const resultadoOptions: { label: string; value: ResultadoTratamento }[] = [
  { label: 'Aprovado', value: 'APROVADO' },
  { label: 'Rejeitado', value: 'REJEITADO' },
  { label: 'Pendente', value: 'PENDENTE' },
];

const ItensTratamentoField: React.FC<{
  value: unknown;
  onChange: (val: { lote: number | null; quantidade: number | null }[]) => void;
}> = ({ value, onChange }) => {
  const itens = Array.isArray(value)
    ? (value as { lote: number | null; quantidade: number | null }[])
    : [];
  const [loteOptions, setLoteOptions] = React.useState<{ value: number; label: string }[]>([]);

  React.useEffect(() => {
    lotesMadeiraService
      .list({ page_size: 100, disponivel_para_tratamento: true })
      .then((res) =>
        setLoteOptions(
          res.results.map((item: any) => ({
            value: item.id,
            label: `${
              item.tipo_madeira_info?.descricao ?? item.tipo_madeira ?? 'Madeira'
            } - ${item.lote_info ?? `Lote ${item.id}`}`,
          })),
        ),
      )
      .catch((err) => {
        console.error('Erro ao carregar lotes para tratamento', err);
      });
  }, []);

  const handleUpdate = (
    index: number,
    patch: Partial<{ lote: number | null; quantidade: number | null }>,
  ) => {
    const next = itens.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    onChange(next);
  };

  const handleAdd = () => {
    onChange([...itens, { lote: null, quantidade: null }]);
  };

  const handleRemove = (index: number) => {
    const next = itens.filter((_, idx) => idx !== index);
    onChange(next);
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <strong>Itens de tratamento</strong>
          <div className="text-muted small">Adicione um ou mais lotes com quantidades.</div>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAdd}>
          Adicionar lote
        </button>
      </div>
      {itens.length === 0 && <div className="text-muted">Nenhum lote adicionado.</div>}
      {itens.map((item, index) => (
        <div key={index} className="d-flex gap-2 align-items-end">
          <div className="flex-grow-1">
            <label className="form-label">Madeira / Lote</label>
            <select
              className="form-select"
              value={item.lote ?? ''}
              onChange={(e) =>
                handleUpdate(index, {
                  lote: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              required
            >
              <option value="">Selecione</option>
              {loteOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: 180 }}>
            <label className="form-label">Quantidade</label>
            <input
              type="number"
              className="form-control"
              value={item.quantidade ?? ''}
              onChange={(e) =>
                handleUpdate(index, {
                  quantidade: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              required
            />
          </div>
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => handleRemove(index)}
            aria-label="Remover item"
          >
            Remover
          </button>
        </div>
      ))}
    </div>
  );
};

const fields: ResourceField<TratamentoMadeira>[] = [
  {
    name: 'tipo_tratamento',
    label: 'Tipo de tratamento',
    type: 'async-select',
    valueType: 'number',
    loadOptions: makeOptionLoader(
      tiposTratamentoService.list,
      (item: any) => `${item.codigo} - ${item.descricao}`,
    ),
    required: true,
  },
  { name: 'data_inicio', label: 'Data de início', type: 'date', required: true },
  { name: 'data_fim', label: 'Data de término', type: 'date' },
  { name: 'etapa', label: 'Etapa' },
  { name: 'controle_qualidade', label: 'Controle de qualidade' },
  { name: 'umidade_saida', label: 'Umidade de saída (%)', type: 'number' },
  { name: 'responsavel', label: 'Responsável' },
  {
    name: 'resultado',
    label: 'Resultado',
    type: 'select',
    options: resultadoOptions,
    required: true,
  },
  {
    name: 'itens',
    label: 'Lotes e quantidades',
    render: ({ value, onChange }) => (
      <ItensTratamentoField
        value={value}
        onChange={(val) => onChange(val ?? [])}
      />
    ),
  },
];

export const TratamentosMadeiraFormPage: React.FC = () => (
  <ResourceFormPage
    title="Tratamento de madeira"
    description="Registre o tratamento aplicado a um lote."
    fields={fields}
    service={tratamentosMadeiraService}
    redirectPath="/admin/aquisicao/tratamentos-madeira"
    cancelPath="/admin/aquisicao/tratamentos-madeira"
  />
);
